import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Select,
  MenuItem,
  ListSubheader,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { Context } from '../../AuthContext';
import { useCategory } from '../../CategoryContext';

const headerStyles = {
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '24px',
  color: '#344054',
  backgroundColor: '#FFFFFF',
  borderBottom: '2px solid #344054',
  paddingX: '16px',
};

const itemStyles = {
  fontWeight: 500,
  fontSize: '14px',
  color: '#344054',
  '&.Mui-selected': { backgroundColor: '#DEEAFF', color: '#006ED4' },
  '&.Mui-selected:hover': { backgroundColor: '#DEEAFF' },
};

// Copy a reference into another vertical the user can edit. Mirrors the card
// "Copy to another vertical" — same editable-verticals filter and grouping — but
// in a dialog with an explicit dropdown + Copy button (per the Figma).
const CopyReferenceModal = ({ open, handleClose, reference }) => {
  const { user } = useContext(Context);
  const { verticals } = useCategory();
  const { vertical_id } = useParams();
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setTarget('');
  }, [open]);

  // Verticals the user can copy into: those they can edit (Creator, or Joined as
  // Editor/AdvancedEditor), excluding the current one. Grouped Private/My/Joined.
  const editableVerticals = (verticals || []).filter(
    (v) =>
      v.id !== vertical_id &&
      (v.status === 'Created' ||
        (v.status === 'Joined' && /^(editor|advancededitor)$/i.test(v.permission || '')))
  );
  const draftVerticals = editableVerticals.filter(
    (v) => v.status === 'Created' && (v.invited_count || 0) === 0
  );
  const myVerticals = editableVerticals.filter(
    (v) => v.status === 'Created' && (v.invited_count || 0) > 0
  );
  const joinedVerticals = editableVerticals.filter((v) => v.status === 'Joined');

  const handleCopy = async () => {
    if (!target || !reference || !user) return;
    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}copy_reference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          _id: reference._id,
          source_vertical_id: vertical_id,
          target_vertical_id: target,
        }),
      });
      if (res.ok) {
        toast.success('Reference copied to vertical', { className: 'toast', autoClose: 3000 });
        handleClose();
      } else {
        toast.error('Failed to copy reference', { className: 'toast', autoClose: 3000 });
      }
    } catch (err) {
      console.error('Failed to copy reference:', err);
      toast.error('Failed to copy reference', { className: 'toast', autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const groupOptions = (label, list) =>
    list.length > 0
      ? [
          <ListSubheader key={`${label}-header`} sx={headerStyles}>
            {label}
          </ListSubheader>,
          ...list.map((v) => (
            <MenuItem key={v.id} value={v.id} sx={itemStyles}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</Box>
            </MenuItem>
          )),
        ]
      : [];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: '26px', fontWeight: 700, textAlign: 'left' }}>
        Copy Reference to Other Vertical
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 18, top: 18, color: '#000' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'left', color: '#000' }}>
        <p style={{ marginTop: 0, marginBottom: '16px' }}>
          Please select which Vertical to copy the selected Reference to.
        </p>

        <Select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          displayEmpty
          fullWidth
          renderValue={(selected) => {
            if (!selected) return <span style={{ color: '#667085' }}>Vertical</span>;
            const v = editableVerticals.find((x) => x.id === selected);
            return v ? v.name : '';
          }}
          sx={{
            height: '48px',
            fontSize: '16px',
            color: '#000',
            borderRadius: '5px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
          }}
        >
          {editableVerticals.length === 0 && (
            <MenuItem disabled value="">
              No editable verticals
            </MenuItem>
          )}
          {groupOptions('Private Verticals', draftVerticals)}
          {groupOptions('My Verticals', myVerticals)}
          {groupOptions('Joined Verticals', joinedVerticals)}
        </Select>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: '20px' }}>
          <Button
            type="button"
            onClick={handleClose}
            style={{
              padding: '6px 18px',
              borderRadius: '5px',
              fontSize: '16px',
              border: '1px solid #356AC3',
              textTransform: 'inherit',
              color: '#356AC3',
              fontWeight: 550,
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCopy}
            disabled={!target || submitting}
            style={{
              backgroundColor: !target || submitting ? '#A0A0A0' : '#356AC3',
              color: '#FFF',
              padding: '8px 24px',
              textTransform: 'inherit',
              borderRadius: '5px',
              fontSize: '16px',
              border: 0,
              fontWeight: 550,
              cursor: !target || submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Copy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CopyReferenceModal;
